import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useRegisterChangeOrderMutation } from '@/lib/api/change-orders';
import { describeApiError } from '@/lib/api/errors';
import { useEngagementDocumentsQuery, type DocumentListItem } from '@/lib/api/documents';
import { cn } from '@/lib/utils/cn';

/** The form's field labels, so a validation error names the field (C2-31). */
const FIELDS: Record<string, string> = {
  document_id: 'Amendment document',
  effective_date: 'Effective date',
};

/**
 * The documents a change order can be registered from (C2-24): processed
 * documents the classifier reads as a change order (its vocabulary files
 * amendments and change requests under `change_order`). Never the SOW
 * being amended, interview notes or anything else; a document classified
 * wrongly is corrected on the Documents screen and then appears here.
 */
export function amendmentDocuments(documents: ReadonlyArray<DocumentListItem>): DocumentListItem[] {
  return documents.filter(
    (d) =>
      (d.process_status === 'indexed' || d.process_status === 'extracted') &&
      d.classification === 'change_order',
  );
}

/**
 * Register a change order (Tier 2 — E06-F02). Picks an already-uploaded
 * amendment/CO document + its effective date; the API supersedes the
 * current SOW chain head and queues the AI diff-extraction run, whose
 * revisions land in the review queue like any other proposal.
 */
export function RegisterChangeOrderModal({
  engagementId,
  open,
  onOpenChange,
  initialDocumentId,
}: {
  engagementId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Preselect this document (used by "Register" on a detected change order). */
  initialDocumentId?: string;
}) {
  const [documentId, setDocumentId] = useState(initialDocumentId ?? '');
  // Each open starts from the document the caller picked (or blank).
  useEffect(() => {
    if (open) setDocumentId(initialDocumentId ?? '');
  }, [open, initialDocumentId]);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);
  const docs = useEngagementDocumentsQuery(open ? engagementId : undefined);
  const register = useRegisterChangeOrderMutation(engagementId);

  const indexedDocs = amendmentDocuments(docs.data?.documents ?? []);

  function submit() {
    setError(null);
    register.mutate(
      {
        body: {
          engagement_id: engagementId,
          document_id: documentId,
          effective_date: effectiveDate,
        },
        idempotencyKey: crypto.randomUUID(),
      },
      {
        onSuccess: () => setQueued(true),
        onError: (e) => setError(describeApiError(e, FIELDS, 'Registration failed.')),
      },
    );
  }

  const inputCls =
    'mt-1 w-full rounded-card border border-ink-200 bg-paper-0 px-2 py-1.5 text-body-sm text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300';

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) setQueued(false);
        onOpenChange(next);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-scrim" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[min(520px,94vw)] -translate-x-1/2 -translate-y-1/2',
            'rounded-modal bg-paper-0 shadow-modal',
          )}
        >
          <div className="flex items-start justify-between gap-3 border-b border-ink-100 bg-paper-50 px-5 py-4">
            <div>
              <Dialog.Title className="text-h2 font-semibold text-ink-900">
                Register change order
              </Dialog.Title>
              <p className="mt-1 text-meta text-ink-500">
                Supersedes the current SOW and queues AI diff extraction
              </p>
            </div>
            <Dialog.Close
              aria-label="Close"
              className="inline-flex h-8 w-8 items-center justify-center rounded-card text-ink-500 hover:bg-paper-100 hover:text-ink-900"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Dialog.Close>
          </div>

          <div className="space-y-4 px-5 py-4">
            {queued ? (
              <p className="rounded-card border border-posture-healthy bg-paper-50 p-3 text-body-sm text-ink-700">
                Change order registered. The AI is extracting revisions against the current baseline
                — proposed changes will appear in the <strong>AI review queue</strong> shortly.
              </p>
            ) : (
              <>
                <label className="block text-meta font-medium text-ink-700">
                  Amendment document (a processed change order)
                  <select
                    value={documentId}
                    onChange={(e) => setDocumentId(e.target.value)}
                    className={inputCls}
                  >
                    <option value="">— select a document —</option>
                    {indexedDocs.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                  {docs.data && indexedDocs.length === 0 ? (
                    <span className="mt-1 block text-accent-amber-700">
                      No change orders yet. Upload the amendment on the Documents screen; once it is
                      read and classified as a change order it appears here. If yours is classified
                      as something else, correct its class there.
                    </span>
                  ) : null}
                </label>
                <label className="block text-meta font-medium text-ink-700">
                  Effective date
                  <input
                    type="date"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                    className={inputCls}
                  />
                </label>
                {error ? (
                  <p className="rounded-card border border-cite-broken bg-paper-0 p-3 text-meta font-medium text-cite-broken">
                    {error}
                  </p>
                ) : null}
              </>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-ink-100 bg-paper-50 px-5 py-3">
            <Dialog.Close asChild>
              <Button type="button" variant="ghost" size="sm">
                {queued ? 'Done' : 'Cancel'}
              </Button>
            </Dialog.Close>
            {!queued ? (
              <Button
                type="button"
                size="sm"
                disabled={!documentId || !effectiveDate || register.isPending}
                onClick={submit}
              >
                {register.isPending ? <Spinner /> : null}
                Register change order
              </Button>
            ) : null}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
